import type {
  AudienceId,
  ContentTypeId,
  GenreMatch,
  WorkAttributeValue,
} from "@/types/taxonomy";

/** 表示モード（男性向け／女性向け） */
export type AudienceType = "male" | "female";

/**
 * 外部商品提供元の識別情報。
 *
 * サイト内部の作品ID（Work.id）とは分離して保持する。
 * 同じ providerId が別サービス（serviceId）に存在しても衝突しないよう、
 * 参照キーは providerKey + serviceId + providerId の組み合わせで作る（lib/workIdentity.ts）。
 */
export interface WorkProviderRef {
  /** 商品提供元の識別子（yumesearch / fanza / dlsite / shopify など） */
  providerKey: string;
  /** 提供元での商品ID（文字列。桁数・形式は固定しない） */
  providerId: string;
  /** 提供元のサービス識別子（動画・同人・通販などの区分） */
  serviceId: string | null;
  /** 提供元の商品フロア識別子 */
  floorId: string | null;
}

export interface Work extends WorkProviderRef {
  /** ユメサーチ内部の一意な作品ID（保存データ・URLはこのIDを使う） */
  id: string;
  title: string;
  alternativeTitles: string[];
  searchKeywords: string[];
  /** 対象ユーザー区分（male / female / common / unclassified） */
  audienceType: AudienceId;
  /** 複数区分を持つ場合の一覧（共通・併記に対応） */
  audienceIds: AudienceId[];
  /** 商品タイプ */
  contentType: ContentTypeId;
  /** 公式ジャンルのID一覧（多対多）。表示時にジャンル名へ解決する */
  genreIds: string[];
  /** 詳細属性（収録時間・ページ数など） */
  attributes: WorkAttributeValue[];
  creator: string;
  performers: string[];
  maker: string;
  series: string | null;
  releaseDate: string;
  addedAt: string;
  updatedAt: string;
  /** 独自タグ（公式ジャンルとは別管理） */
  tags: string[];
  description: string;
  thumbnailUrl: string;
  sampleVideoUrl: string | null;
  price: number;
  onSale: boolean;
  rating: number | null;
  reviewCount: number | null;
  rankingScore: number | null;
  affiliateUrl: string;
  sourceName: string | null;
  sourceUpdatedAt: string | null;
}

export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  publishedAt: string;
  updatedAt: string;
  imageUrl: string;
  body: string[];
  relatedWorkIds: string[];
}

export type PriceBand = "free" | "under500" | "500to1000" | "1000to3000" | "over3000";

export type SeriesFilter = "single" | "series";

/** 発売状況（発売済み／発売予定の切り替え） */
export type ReleaseStatusFilter = "any" | "released" | "upcoming";

export type SortKey =
  | "relevance"
  | "popular"
  | "newest"
  | "oldest"
  | "priceAsc"
  | "priceDesc"
  | "rating";

/** 属性（収録時間・ページ数など）の数値レンジ条件 */
export interface AttributeFilter {
  attributeId: string;
  /** 下限（含む）。null は下限なし */
  min: number | null;
  /** 上限（含む）。null は上限なし */
  max: number | null;
}

export interface WorkFilters {
  q: string;
  /** 対象ユーザー（all は現在のモードに共通区分を加えたもの） */
  audience: AudienceId | "all";
  types: ContentTypeId[];
  /** 公式ジャンルのID一覧 */
  genres: string[];
  /** ジャンルの照合方法（同一項目内の複数選択時のAND / OR） */
  genreMatch: GenreMatch;
  person: string;
  makers: string[];
  /** 直近N日（発売済みのみ。発売予定は含めない） */
  days: number | null;
  /** 発売状況（any は指定なし） */
  releaseStatus: ReleaseStatusFilter;
  priceBands: PriceBand[];
  minRating: number | null;
  video: "yes" | "no" | null;
  sale: "yes" | "no" | null;
  series: SeriesFilter | null;
  /** 選択中の商品形式に応じて出し分ける属性レンジ条件 */
  attributes: AttributeFilter[];
}

export const EMPTY_FILTERS: WorkFilters = {
  q: "",
  audience: "all",
  types: [],
  genres: [],
  genreMatch: "or",
  person: "",
  makers: [],
  days: null,
  releaseStatus: "any",
  priceBands: [],
  minRating: null,
  video: null,
  sale: null,
  series: null,
  attributes: [],
};